// Accounts Page Component - 계정별 분석
import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import {
  Users,
  User,
  Eye,
  Heart,
  MessageCircle,
  TrendingUp,
  X,
  BarChart3,
  RefreshCw,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { translations } from "@/i18n/translations";
import { 
  API, 
  PLATFORMS,
  PLATFORM_CHART_COLORS,
  formatNumber, 
  getPlatformBadgeClass,
} from "./constants";
import { MetricCard } from "./CommonComponents";
import PostsTable from "./PostsTable";
import { useAuth } from "@/contexts/AuthContext";

// Platforms that don't provide view counts
const VIEWS_UNAVAILABLE_PLATFORMS = ["weibo", "xiaohongshu", "instagram", "douyin"];

// Normalize group name - prefer account.group field, fall back to display_name matching
const normalizeGroupName = (account) => {
  // Use the group field from DB if available
  if (account.group && account.group.trim()) {
    return account.group;
  }
  
  const displayName = account.display_name || account;
  if (!displayName) return "Other";
  
  // Normalize underscores to spaces and handle case variations
  const normalized = (typeof displayName === "string" ? displayName : "").replace(/_/g, " ").trim().toLowerCase();
  
  // Map to canonical group names
  if (normalized.includes("a2o") && normalized.includes("may")) {
    return "A2O MAY";
  }
  if (normalized.includes("a2o") && (normalized.includes("channel") || normalized.includes("world"))) {
    return "A2O Channel";
  }
  
  return (typeof displayName === "string" ? displayName : "").replace(/_/g, " ").trim();
};

const AccountsPage = ({ onSelectAccount, lang = "en", startDate, endDate }) => {
  const t = translations[lang] || translations.en;
  const { isAdmin } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [accountPosts, setAccountPosts] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState("all");
  const [syncingAccounts, setSyncingAccounts] = useState(new Set());

  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build posts URL with date filters
      let postsUrl = `${API}/posts?limit=500`;
      if (startDate) {
        postsUrl += `&start_date=${format(startDate, "yyyy-MM-dd")}`;
      }
      if (endDate) {
        postsUrl += `&end_date=${format(endDate, "yyyy-MM-dd")}`;
      }
      
      const [accountsRes, postsRes] = await Promise.all([
        axios.get(`${API}/accounts`),
        axios.get(postsUrl),
      ]);
      
      setAccounts(accountsRes.data);
      
      const postsByAccount = {};
      for (const post of postsRes.data) {
        if (!postsByAccount[post.account_id]) {
          postsByAccount[post.account_id] = [];
        }
        postsByAccount[post.account_id].push(post);
      }
      setAccountPosts(postsByAccount);
    } catch (error) {
      toast.error("Failed to load account data");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const handleSyncAccount = async (e, accountId, displayName) => {
    e.stopPropagation();
    setSyncingAccounts(prev => new Set(prev).add(accountId));
    try {
      const res = await axios.post(`${API}/sync/account/${accountId}`);
      toast.success(`${displayName}: ${res.data.posts_synced || 0} ${lang === 'ko' ? '게시물 동기화 완료' : lang === 'zh' ? '帖子已同步' : 'posts synced'}`);
      fetchAccounts();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(`${displayName}: ${msg}`);
    } finally {
      setSyncingAccounts(prev => {
        const next = new Set(prev);
        next.delete(accountId);
        return next;
      });
    }
  };

  // Get stats from posts (for detailed views)
  const getAccountStats = (accountId) => {
    const posts = accountPosts[accountId] || [];
    const totalViews = posts.reduce((sum, p) => sum + (p.views || 0), 0);
    const totalLikes = posts.reduce((sum, p) => sum + (p.likes || 0), 0);
    const totalComments = posts.reduce((sum, p) => sum + (p.comments || 0), 0);
    return { totalViews, totalLikes, totalComments, postCount: posts.length };
  };

  // Format engagement score based on type from backend
  const formatEngagementScore = (account) => {
    const score = account.avg_engagement_score;
    const type = account.engagement_type;
    
    if (score === null || score === undefined) {
      return "-";
    }
    
    if (type === "weighted") {
      // Weighted sum score for Xiaohongshu, Weibo, Douyin
      return score.toLocaleString();
    } else {
      // Percentage-based engagement rate for YouTube, TikTok, etc.
      return `${score.toFixed(2)}%`;
    }
  };

  const groupedAccounts = useMemo(() => {
    return accounts.reduce((groups, account) => {
      const groupName = normalizeGroupName(account);
      if (!groups[groupName]) {
        groups[groupName] = [];
      }
      groups[groupName].push(account);
      return groups;
    }, {});
  }, [accounts]);

  // Get unique group names for filter buttons
  const groupNames = useMemo(() => {
    return Object.keys(groupedAccounts).sort();
  }, [groupedAccounts]);

  // Prepare data for followers comparison chart
  const followersChartData = useMemo(() => {
    let filteredAccounts = accounts;
    
    // Filter by selected group
    if (selectedGroup !== "all") {
      filteredAccounts = accounts.filter(a => normalizeGroupName(a) === selectedGroup);
    }
    
    // Sort by followers descending and map to chart format
    return filteredAccounts
      .sort((a, b) => (b.followers || 0) - (a.followers || 0))
      .map(a => ({
        name: `${a.platform}`,
        fullName: `${a.display_name} (${a.platform})`,
        followers: a.followers || 0,
        platform: a.platform,
        displayName: a.display_name,
      }));
  }, [accounts, selectedGroup]);

  if (selectedAccount) {
    const account = accounts.find(a => a.id === selectedAccount);
    const posts = accountPosts[selectedAccount] || [];
    const stats = getAccountStats(selectedAccount);

    return (
      <div className="space-y-6" data-testid="account-detail-page">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => setSelectedAccount(null)} data-testid="back-to-accounts">
            <X className="h-4 w-4 mr-2" />
            {t.accounts.back}
          </Button>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className={`${getPlatformBadgeClass(account?.platform)} capitalize text-sm`}>
              {account?.platform}
            </Badge>
            <h2 className="font-heading font-bold text-xl">{account?.display_name}</h2>
            <span className="text-muted-foreground">@{account?.username}</span>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <MetricCard title={t.metrics.followers} value={formatNumber(account?.followers || 0)} icon={Users} color="#3B82F6" />
          {VIEWS_UNAVAILABLE_PLATFORMS.includes(account?.platform) ? (
            <MetricCard title={t.metrics.totalLikes} value={formatNumber(stats.totalLikes)} icon={Heart} color="#EF4444" />
          ) : (
            <MetricCard title={t.metrics.totalViews} value={formatNumber(stats.totalViews)} icon={Eye} color="#10B981" />
          )}
          {!VIEWS_UNAVAILABLE_PLATFORMS.includes(account?.platform) && (
            <MetricCard title={t.metrics.totalLikes} value={formatNumber(stats.totalLikes)} icon={Heart} color="#EF4444" />
          )}
          <MetricCard title={t.metrics.comments} value={formatNumber(stats.totalComments)} icon={MessageCircle} color="#F59E0B" />
          <MetricCard title={t.metrics.avgEngagement} value={formatEngagementScore(account)} icon={TrendingUp} color="#8B5CF6" />
        </div>

        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="text-lg font-heading">{t.posts.postList} ({stats.postCount})</CardTitle>
          </CardHeader>
          <CardContent>
            <PostsTable posts={posts} loading={false} lang={lang} accounts={accounts} />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="accounts-page">
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="skeleton h-48 rounded-lg" />
          ))}
        </div>
      ) : (
        <>
          {/* Followers Comparison Chart */}
          <Card className="bg-card border-border/50">
            <CardHeader className="pb-2">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-primary" />
                  {lang === 'ko' ? '팔로워 비교' : lang === 'zh' ? '粉丝对比' : 'Followers Comparison'}
                </CardTitle>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant={selectedGroup === "all" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedGroup("all")}
                    data-testid="filter-all"
                  >
                    {lang === 'ko' ? '전체' : lang === 'zh' ? '全部' : 'All'}
                  </Button>
                  {groupNames.map(group => (
                    <Button
                      key={group}
                      variant={selectedGroup === group ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSelectedGroup(group)}
                      data-testid={`filter-${group.replace(/\s/g, '-')}`}
                    >
                      {group}
                    </Button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={Math.max(280, followersChartData.length * 45)}>
                <BarChart 
                  data={followersChartData} 
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={true} vertical={false} />
                  <XAxis 
                    type="number" 
                    stroke="hsl(var(--muted-foreground))" 
                    fontSize={11} 
                    tickFormatter={formatNumber}
                  />
                  <YAxis 
                    type="category" 
                    dataKey="fullName" 
                    stroke="hsl(var(--muted-foreground))" 
                    fontSize={11}
                    width={95}
                    tick={{ fill: 'hsl(var(--foreground))' }}
                  />
                  <Tooltip
                    contentStyle={{ 
                      backgroundColor: "hsl(var(--popover))", 
                      border: "1px solid hsl(var(--border))", 
                      borderRadius: "8px",
                      padding: "8px 12px",
                      color: "hsl(var(--popover-foreground))"
                    }}
                    labelStyle={{
                      color: "hsl(var(--popover-foreground))",
                      fontWeight: 600,
                      marginBottom: "4px"
                    }}
                    itemStyle={{
                      color: "hsl(var(--popover-foreground))"
                    }}
                    formatter={(value) => [formatNumber(value), lang === 'ko' ? '팔로워' : lang === 'zh' ? '粉丝' : 'Followers']}
                    labelFormatter={(label) => label}
                  />
                  <Bar 
                    dataKey="followers" 
                    radius={[0, 4, 4, 0]}
                    maxBarSize={35}
                    stroke="hsl(var(--border))"
                    strokeWidth={1}
                  >
                    {followersChartData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={PLATFORM_CHART_COLORS[entry.platform] || "#6366F1"} 
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              
              {/* Platform Legend */}
              <div className="flex flex-wrap justify-center gap-4 mt-4 pt-4 border-t border-border/50">
                {Object.entries(PLATFORM_CHART_COLORS).map(([platform, color]) => {
                  const hasAccount = followersChartData.some(d => d.platform === platform);
                  if (!hasAccount) return null;
                  return (
                    <div key={platform} className="flex items-center gap-1.5 text-xs">
                      <div 
                        className="w-3 h-3 rounded-sm" 
                        style={{ backgroundColor: color }}
                      />
                      <span className="capitalize text-muted-foreground">{platform}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Account Groups */}
          {Object.entries(groupedAccounts).map(([groupName, groupAccounts]) => (
            <div key={groupName} className="space-y-4">
              <h3 className="font-heading font-bold text-lg flex items-center gap-2">
                <User className="h-5 w-5 text-primary" />
                {groupName}
                <Badge variant="secondary" className="ml-2 font-normal">
                  {groupAccounts.length} {lang === 'ko' ? '계정' : lang === 'zh' ? '账户' : 'accounts'}
                </Badge>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {groupAccounts.map((account) => {
                  const stats = getAccountStats(account.id);
                  const platformInfo = PLATFORMS.find(p => p.id === account.platform);
                  
                  return (
                    <Card 
                      key={account.id} 
                      className="bg-card border-border/50 hover:border-primary/50 transition-colors cursor-pointer"
                      onClick={() => setSelectedAccount(account.id)}
                      data-testid={`account-card-${account.id}`}
                    >
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <Badge 
                            variant="outline" 
                            className={`${getPlatformBadgeClass(account.platform)} capitalize`}
                            style={{ borderColor: platformInfo?.color }}
                          >
                            {account.platform}
                          </Badge>
                          <div className="flex items-center gap-2">
                            {isAdmin && (
                            <button
                              onClick={(e) => handleSyncAccount(e, account.id, account.display_name)}
                              disabled={syncingAccounts.has(account.id)}
                              className="p-1.5 rounded-md hover:bg-muted transition-colors disabled:opacity-50"
                              data-testid={`sync-btn-${account.id}`}
                              title={lang === 'ko' ? '동기화' : lang === 'zh' ? '同步' : 'Sync'}
                            >
                              <RefreshCw className={`h-3.5 w-3.5 text-muted-foreground ${syncingAccounts.has(account.id) ? 'animate-spin' : ''}`} />
                            </button>
                            )}
                            <span className="text-xs text-muted-foreground">@{account.username}</span>
                          </div>
                        </div>
                        <CardTitle className="text-base mt-2">{account.display_name}</CardTitle>
                        {account.last_synced && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {lang === 'ko' ? '마지막 동기화' : lang === 'zh' ? '最近同步' : 'Last sync'}: {new Date(account.last_synced).toLocaleString()}
                          </p>
                        )}
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="text-muted-foreground text-xs">{t.metrics.followers}</p>
                            <p className="font-mono font-semibold">{formatNumber(account.followers)}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground text-xs">{t.metrics.posts}</p>
                            <p className="font-mono font-semibold">{stats.postCount}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground text-xs">
                              {VIEWS_UNAVAILABLE_PLATFORMS.includes(account.platform) 
                                ? t.metrics.totalLikes 
                                : t.metrics.totalViews}
                            </p>
                            <p className="font-mono font-semibold">
                              {VIEWS_UNAVAILABLE_PLATFORMS.includes(account.platform)
                                ? formatNumber(stats.totalLikes)
                                : formatNumber(stats.totalViews)}
                            </p>
                          </div>
                          <div>
                            <p className="text-muted-foreground text-xs">{t.metrics.avgEngagement}</p>
                            <p className="font-mono font-semibold text-primary" data-testid={`engagement-score-${account.id}`}>
                              {formatEngagementScore(account)}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default AccountsPage;
